import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AppLayout from '../layout/AppLayout';
import { auditService } from '../../services/api';
import { INK, MUTED, RULE, formatCount } from '../analytics/analyticsUi';

const PAGE_SIZE = 50;
const FAIL_COLOR = '#C2410C';
const buttonSx = { textTransform: 'none' };

const formatWhen = (value) => new Date(value).toLocaleString('en-IN', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true
});

// "6ab37168c6cca7ffbfd15a0f" is unreadable in full; the tail is enough to match a record
const shortId = (id) => (id && id.length > 10 ? `…${id.slice(-6)}` : id);

const EntryRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = !!entry.body || !!entry.userAgent || !!entry.ip;

  return (
    <>
      <TableRow hover sx={{ '& td': { borderColor: RULE, verticalAlign: 'top' } }}>
        <TableCell sx={{ pl: { xs: 2, sm: 3 }, whiteSpace: 'nowrap', color: INK, fontVariantNumeric: 'tabular-nums' }}>
          {formatWhen(entry.at)}
        </TableCell>
        <TableCell sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: INK }} noWrap title={entry.actor?.email}>
            {entry.actor?.name || 'Unknown'}
          </Typography>
          <Typography variant="caption" sx={{ color: MUTED }} noWrap>{entry.actor?.email}</Typography>
        </TableCell>
        <TableCell sx={{ color: INK }}>
          {entry.action}
          <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{entry.method} {entry.route}</Typography>
        </TableCell>
        <TableCell sx={{ color: MUTED, whiteSpace: 'nowrap' }}>
          {entry.target?.id ? (
            <Tooltip title={entry.target.id}>
              <Box component="span">{entry.target.type ? `${entry.target.type} ` : ''}{shortId(entry.target.id)}</Box>
            </Tooltip>
          ) : '–'}
        </TableCell>
        <TableCell sx={{ color: entry.success ? INK : FAIL_COLOR, maxWidth: 260 }}>
          {entry.success ? 'Done' : `Failed: ${entry.error || entry.status}`}
        </TableCell>
        <TableCell sx={{ pr: { xs: 2, sm: 3 }, width: 48 }}>
          {hasDetail && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? 'Hide details' : 'Show details'}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? `1px solid ${RULE}` : 'none' }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
                {entry.body && (
                  <>
                    <Typography variant="caption" sx={{ color: MUTED, display: 'block', mb: 0.5 }}>
                      What was submitted (passwords and tokens are never stored)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{ m: 0, mb: 1.5, p: 1.5, bgcolor: '#F7F8FA', borderRadius: 1, fontSize: '0.8rem', color: INK, overflowX: 'auto' }}
                    >
                      {typeof entry.body === 'string' ? entry.body : JSON.stringify(entry.body, null, 2)}
                    </Box>
                  </>
                )}
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {entry.path}
                  {entry.ip && ` · from ${entry.ip}`}
                  {entry.userAgent && ` · ${entry.userAgent}`}
                </Typography>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AuditLog = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [outcome, setOutcome] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const params = useCallback(() => ({
    page,
    limit: PAGE_SIZE,
    ...(actor ? { actor } : {}),
    ...(action ? { action } : {}),
    ...(outcome ? { success: outcome === 'done' } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(query ? { q: query } : {})
  }), [page, actor, action, outcome, from, to, query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    auditService.list(params())
      .then((response) => { if (!cancelled) setData(response.data); })
      .catch(() => { if (!cancelled) setError('The audit log could not be loaded. Try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params]);

  // Filters other than the page reset paging, so a filter never lands on an empty page
  const onFilter = (setter) => (value) => { setter(value); setPage(1); };

  const handleExport = async () => {
    try {
      const blob = await auditService.exportCsv({ ...params(), page: undefined, limit: undefined });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError('The export could not be created. Try again.');
    }
  };

  const entries = data?.entries || [];
  const filtered = !!(actor || action || outcome || from || to || query);

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
              Audit log
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
              Every change an administrator makes, kept for 30 days.
              {data && ` ${formatCount(data.total)} ${data.total === 1 ? 'entry' : 'entries'}${filtered ? ' for this filter' : ''}.`}
            </Typography>
          </Box>
          <Button variant="outlined" onClick={handleExport} disabled={!entries.length} sx={buttonSx}>
            Export CSV
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
          <TextField
            size="small"
            placeholder="Search action, admin or record"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setQuery(search); setPage(1); } }}
            sx={{ minWidth: 260, flexGrow: 1, maxWidth: 380 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: MUTED }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(''); setQuery(''); setPage(1); }} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="audit-actor">Administrator</InputLabel>
            <Select labelId="audit-actor" label="Administrator" value={actor} onChange={(e) => onFilter(setActor)(e.target.value)}>
              <MenuItem value="">Any administrator</MenuItem>
              {(data?.actors || []).map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="audit-action">Action</InputLabel>
            <Select labelId="audit-action" label="Action" value={action} onChange={(e) => onFilter(setAction)(e.target.value)}>
              <MenuItem value="">Any action</MenuItem>
              {(data?.actions || []).map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="audit-outcome">Outcome</InputLabel>
            <Select labelId="audit-outcome" label="Outcome" value={outcome} onChange={(e) => onFilter(setOutcome)(e.target.value)}>
              <MenuItem value="">Any outcome</MenuItem>
              <MenuItem value="done">Done</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => onFilter(setFrom)(e.target.value)} sx={{ width: 160 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => onFilter(setTo)(e.target.value)} sx={{ width: 160 }} />
        </Box>

        <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>
          {loading && !data ? (
            <Box sx={{ p: 2 }}><Skeleton variant="rounded" height={320} /></Box>
          ) : !entries.length ? (
            <Box sx={{ p: { xs: 3, sm: 5 } }}>
              <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Nothing recorded</Typography>
              <Typography variant="body2" sx={{ color: MUTED, maxWidth: '60ch' }}>
                {filtered
                  ? 'No admin actions match this filter. Try a wider date range.'
                  : 'No administrator has changed anything yet. Actions appear here as they happen.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    {['When', 'Administrator', 'Action', 'Record', 'Outcome', ''].map((label, i) => (
                      <TableCell
                        key={label || 'expand'}
                        sx={{ color: MUTED, fontWeight: 500, borderColor: RULE, whiteSpace: 'nowrap', pl: i === 0 ? { xs: 2, sm: 3 } : undefined }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => <EntryRow key={entry._id} entry={entry} />)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {data && data.pages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Typography variant="body2" sx={{ color: MUTED }}>Page {data.page} of {data.pages}</Typography>
            <Button size="small" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} sx={buttonSx}>Previous</Button>
            <Button size="small" disabled={page >= data.pages || loading} onClick={() => setPage((p) => p + 1)} sx={buttonSx}>Next</Button>
          </Box>
        )}

        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default AuditLog;
